import { Braces, Check, Copy, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateTokenDialog } from '@/Domain/components/Integration/ApiIntegration/CreateTokenDialog';
import { UncopiedTokenDialog } from '@/Domain/components/Integration/ApiIntegration/components/UncopiedTokenDialog';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Card } from '@/Domain/components/Shared/Card/Card';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { UpgradeBadge } from '@/Domain/components/Shared/UpgradeBadge/UpgradeBadge';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { useApiTokens } from '@/Domain/hooks/Integration/useApiTokens';
import { useModalStore } from '@/Domain/store/modal.store';
import { SCENARIO_ID } from '@/context';

export const API_INTEGRATION_SHEET = 'API_INTEGRATION_SHEET';

export const ApiIntegrationSheet = () => {
  const { t } = useTranslation(['integrations']);
  const open = useModalStore(useCallback((state) => state.isOpen(API_INTEGRATION_SHEET), []));
  const closeModal = useModalStore((state) => state.closeModal);

  const { getConfig, isFetched: isSubscriptionFetched } = useSubscription();
  // `allowed_integrations` is a list of allowed integration names; tolerate a
  // dict shape too so a payload change can't silently gate paying users.
  const rawAllowedIntegrations = getConfig('allowed_integrations');
  const isApiAllowed = Array.isArray(rawAllowedIntegrations)
    ? rawAllowedIntegrations.includes('api')
    : !!(rawAllowedIntegrations as Record<string, boolean> | undefined)?.api;
  const isApiUnavailable = isSubscriptionFetched && !isApiAllowed;

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const {
    activeTokens,
    createToken,
    isCreating,
    revokeToken,
    isRevoking,
    copiedTokens,
    handleCopyToken,
    hasUncopiedPendingTokens,
  } = useApiTokens(open);

  const formatDate = (value: any): string => {
    if (!value) return '-';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
  };

  const getDaysUntilExpiration = (expirationTimestamp?: string): number | null => {
    if (!expirationTimestamp) return null;
    const expiration = new Date(expirationTimestamp);
    if (Number.isNaN(expiration.getTime())) return null;
    const now = new Date();
    const diffMs = expiration.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const isTokenExpired = (expirationTimestamp?: string): boolean => {
    if (!expirationTimestamp) return false;
    const expiration = new Date(expirationTimestamp);
    if (Number.isNaN(expiration.getTime())) return false;
    return expiration.getTime() < new Date().getTime();
  };

  const getExpirationText = (expirationTimestamp?: string): string => {
    const days = getDaysUntilExpiration(expirationTimestamp);
    if (days === null)
      return t('integrations:api_integration.sheet.active_tokens.expiration.never');
    if (days < 0) return t('integrations:api_integration.sheet.active_tokens.expiration.expired');
    if (days === 0) return t('integrations:api_integration.sheet.active_tokens.expiration.today');
    if (days === 1)
      return t('integrations:api_integration.sheet.active_tokens.expiration.tomorrow');
    return t('integrations:api_integration.sheet.active_tokens.expiration.days', { count: days });
  };

  const handleCreateToken = useCallback(
    (description: string, scopes: string[]) => {
      createToken({ description, scopes });
      setCreateDialogOpen(false);
    },
    [createToken],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      const currentOpen = useModalStore.getState().isOpen(API_INTEGRATION_SHEET);
      if (!currentOpen || nextOpen === currentOpen) return;

      if (!nextOpen) {
        if (hasUncopiedPendingTokens) {
          setConfirmationModalOpen(true);
        } else {
          closeModal();
        }
      }
    },
    [hasUncopiedPendingTokens, closeModal],
  );

  const handleConfirmationOpenChange = useCallback((nextOpen: boolean) => {
    const currentOpen = useModalStore.getState().isOpen(API_INTEGRATION_SHEET);
    if (!currentOpen) return;
    setConfirmationModalOpen(nextOpen);
  }, []);

  const handleConfirmClose = useCallback(() => {
    const currentOpen = useModalStore.getState().isOpen(API_INTEGRATION_SHEET);
    if (!currentOpen) return;
    setConfirmationModalOpen(false);
    closeModal();
  }, [closeModal]);

  useEffect(() => {
    if (!open) {
      setConfirmationModalOpen(false);
      setCreateDialogOpen(false);
    }
  }, [open]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const currentOpen = useModalStore.getState().isOpen(API_INTEGRATION_SHEET);
      if (!currentOpen) return;
      if (e.key === 'Escape') {
        if (confirmationModalOpen) return;
        e.preventDefault();
        e.stopPropagation();
        // First Escape closes the create-token modal; the next closes the sheet.
        if (createDialogOpen) {
          setCreateDialogOpen(false);
        } else {
          handleOpenChange(false);
        }
      }
    },
    [confirmationModalOpen, createDialogOpen, handleOpenChange],
  );

  useEffect(() => {
    if (!open) return;
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [open, handleKeyDown]);

  const apiPrerequisites = useMemo(() => {
    const items = t('integrations:api_integration.prerequisites', {
      returnObjects: true,
    }) as string[];
    return [
      <span key="scenario">
        {items[0]} <span className="font-mono font-semibold text-foreground">{SCENARIO_ID}</span>
      </span>,
      ...items.slice(1),
    ];
  }, [t]);

  const existingDescriptions = useMemo(
    () => activeTokens.map((token) => token.description),
    [activeTokens],
  );

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        className="max-w-xl w-full h-full pb-0 space-y-0"
        tabIndex={undefined}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          if (createDialogOpen) {
            setCreateDialogOpen(false);
          } else {
            handleOpenChange(false);
          }
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex items-center gap-3">
            <Braces className="size-8 shrink-0" />
            <div className="flex flex-col gap-1">
              <SheetTitle data-testid="api-access-title">
                {t('integrations:api_integration.sheet.title')}
              </SheetTitle>
            </div>
          </div>
          <section className="flex items-center gap-2">
            <IconButton
              onClick={() => handleOpenChange(false)}
              variant="secondary"
              data-testid="close-sheet-button"
              className="shrink-0"
            >
              <CloseIcon />
            </IconButton>
          </section>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6">
          <IntegrationIntroduction
            description={t('integrations:api_integration.overview')}
            prerequisites={apiPrerequisites}
            docsHref="https://docs.quickchat.ai/api-reference/chat"
          />

          <IntegrationSection
            title={t('integrations:api_integration.sheet.tokens_section.title')}
            titleAddon={isApiUnavailable ? <UpgradeBadge /> : undefined}
            data-testid="api-connect-enable-section"
          >
            <Button
              variant="dark"
              size="sm"
              className="w-full"
              onClick={() => setCreateDialogOpen(true)}
              disabled={isApiUnavailable}
              data-testid="create-token-button"
            >
              <Plus className="size-4" />
              {t('integrations:api_integration.sheet.tokens_section.create_button')}
            </Button>

            <div className="space-y-3">
              {activeTokens.length === 0 ? (
                <div
                  className="text-sm text-foreground-subtle py-8 text-center"
                  data-testid="empty-tokens-message"
                >
                  {t('integrations:api_integration.sheet.tokens_section.empty_message')}
                </div>
              ) : (
                <div className="space-y-3">
                  {activeTokens.map((token) => {
                    const expired = isTokenExpired(token.expiration_timestamp);
                    const expirationText = getExpirationText(token.expiration_timestamp);

                    return (
                      <Card
                        key={token.token_uuid}
                        data-testid={`token-card-${token.token_uuid}`}
                        className={`p-4 gap-3 ${
                          expired ? 'border-warning-muted bg-warning-muted' : ''
                        }`}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-3 items-start">
                          <div className="md:col-span-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-foreground-subtle font-normal">
                                {t(
                                  'integrations:api_integration.sheet.active_tokens.card.description',
                                )}
                              </Label>
                              {token.token && (
                                <Badge variant="secondary" size="xs">
                                  {t(
                                    'integrations:api_integration.sheet.active_tokens.card.badge.new',
                                  )}
                                </Badge>
                              )}
                            </div>
                            <p
                              className="text-sm text-foreground mt-0.5 truncate"
                              data-testid={`token-description-${token.token_uuid}`}
                              title={token.description}
                            >
                              {token.description}
                            </p>
                          </div>
                          <div className="md:col-span-1 min-w-0">
                            <Label className="text-xs text-foreground-subtle font-normal">
                              {t(
                                'integrations:api_integration.sheet.active_tokens.card.permission',
                              )}
                            </Label>
                            <p
                              className="text-sm text-foreground mt-0.5"
                              data-testid={`token-scope-${token.token_uuid}`}
                            >
                              {token.scopes?.includes('write_all')
                                ? t(
                                    'integrations:api_integration.sheet.active_tokens.card.scope_read_write',
                                  )
                                : t(
                                    'integrations:api_integration.sheet.active_tokens.card.scope_read_only',
                                  )}
                            </p>
                          </div>
                          <div className="md:col-span-1">
                            <Label className="text-xs text-foreground-subtle font-normal">
                              {t('integrations:api_integration.sheet.active_tokens.card.created')}
                            </Label>
                            <p
                              className="text-sm text-foreground mt-0.5"
                              data-testid={`token-created-${token.token_uuid}`}
                            >
                              {formatDate(token.created_timestamp)}
                            </p>
                          </div>
                          <div className="md:col-span-1">
                            <Label className="text-xs text-foreground-subtle font-normal">
                              {t(
                                'integrations:api_integration.sheet.active_tokens.card.expires_in',
                              )}
                            </Label>
                            <p
                              className={`text-sm mt-0.5 ${expired ? 'text-warning-foreground font-semibold' : 'text-foreground'}`}
                              data-testid={`token-expiration-${token.token_uuid}`}
                            >
                              {expirationText}
                            </p>
                          </div>
                          <div className="flex justify-end self-center">
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => revokeToken(token.token_uuid)}
                              disabled={isRevoking(token.token_uuid)}
                              data-testid={`revoke-button-${token.token_uuid}`}
                              aria-label={t(
                                'integrations:api_integration.sheet.active_tokens.card.revoke',
                              )}
                              title={t(
                                'integrations:api_integration.sheet.active_tokens.card.revoke',
                              )}
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          </div>
                        </div>

                        {token.token && (
                          <div className="pt-3 border-t border-border space-y-1.5">
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-foreground-subtle font-normal">
                                {t('integrations:api_integration.sheet.active_tokens.card.token')}
                              </Label>
                              <Badge variant="warning" size="xs">
                                {t(
                                  'integrations:api_integration.sheet.active_tokens.card.badge.shown_once',
                                )}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="dark"
                                size="sm"
                                onClick={() => handleCopyToken(token.token!)}
                                data-testid={`copy-token-button-${token.token_uuid}`}
                              >
                                {copiedTokens.has(token.token) ? (
                                  <Check className="size-4" />
                                ) : (
                                  <Copy className="size-4" />
                                )}
                                {copiedTokens.has(token.token)
                                  ? t(
                                      'integrations:api_integration.sheet.active_tokens.card.copied',
                                    )
                                  : t('integrations:api_integration.sheet.active_tokens.card.copy')}
                              </Button>
                              <Input
                                type="text"
                                size="sm"
                                value={token.token}
                                readOnly
                                className="flex-1 font-mono text-muted-foreground text-sm"
                                data-testid={`token-value-${token.token_uuid}`}
                              />
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </IntegrationSection>
        </div>
        <CreateTokenDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          isCreating={isCreating}
          onSubmit={handleCreateToken}
          existingDescriptions={existingDescriptions}
        />
        <UncopiedTokenDialog
          open={confirmationModalOpen}
          onOpenChange={handleConfirmationOpenChange}
          onConfirmClose={handleConfirmClose}
        />
      </SheetContent>
    </Sheet>
  );
};
